const FLOATING_ID = 'NoHeroFloatingLogo';
const HANDOFF_AT = 0.92;

/**
 * @param {number} value
 * @returns {number}
 */
function clamp01(value) {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

function initHeroLogoTransition() {
  const section = document.querySelector('[data-hero-section]');
  const wrapper = document.querySelector('[data-hero-floating-wrapper]');

  if (!(section instanceof HTMLElement) || !(wrapper instanceof HTMLElement)) return;

  // Respect reduced motion: keep the accessible static logo + normal header.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const link = wrapper.querySelector('[data-hero-floating-link]');
  const darkImg = wrapper.querySelector('[data-hero-logo-dark]');
  const lightImg = wrapper.querySelector('[data-hero-logo-light]');
  const staticLogo = section.querySelector('[data-hero-static-logo]');
  const headerComponent = document.querySelector('#header-component');
  const headerLogo = document.querySelector('#header-component .header-logo');

  if (
    !(link instanceof HTMLElement) ||
    !(darkImg instanceof HTMLImageElement) ||
    !(lightImg instanceof HTMLImageElement) ||
    !(headerComponent instanceof HTMLElement) ||
    !(headerLogo instanceof HTMLElement)
  ) {
    return;
  }

  const sectionEl = section;
  const wrapperEl = wrapper;
  const linkEl = link;
  const darkImgEl = darkImg;
  const lightImgEl = lightImg;
  const headerEl = headerComponent;
  const headerLogoEl = headerLogo;

  const existing = document.getElementById(FLOATING_ID);
  if (existing && existing !== wrapper) existing.remove();
  wrapper.id = FLOATING_ID;
  document.body.appendChild(wrapper);

  // Reveal the floating overlay (avoid `.hidden` — base.css uses display:none !important).
  wrapperEl.classList.remove('hidden', 'invisible', 'opacity-0');
  wrapperEl.style.opacity = '1';
  wrapperEl.style.visibility = 'visible';
  wrapperEl.style.pointerEvents = 'none';

  if (staticLogo instanceof HTMLElement) staticLogo.style.display = 'none';
  headerLogoEl.style.visibility = 'hidden';
  headerEl.style.transition = 'none';

  /** @type {DOMRect | null} */
  let startRect = null;
  /** @type {DOMRect | null} */
  let targetRect = null;
  let ticking = false;
  let footerHiddenByHeader = headerEl.dataset.footerVisible === 'true';

  function measureTarget() {
    const previous = headerEl.dataset.stickyState;
    headerEl.dataset.stickyState = 'active';
    targetRect = headerLogoEl.getBoundingClientRect();
    if (previous) {
      headerEl.dataset.stickyState = previous;
    } else {
      delete headerEl.dataset.stickyState;
    }
  }

  function measure() {
    linkEl.style.transform = 'none';
    startRect = linkEl.getBoundingClientRect();
    measureTarget();
  }

  function hideFloatingLogo() {
    linkEl.style.transform = '';
    darkImgEl.style.opacity = '0';
    lightImgEl.style.opacity = '0';
    wrapperEl.style.opacity = '0';
    wrapperEl.style.visibility = 'hidden';
  }

  function clearHeaderInlineStyles() {
    headerEl.style.opacity = '';
    headerEl.style.pointerEvents = '';
    headerLogoEl.style.visibility = '';
  }

  function render() {
    ticking = false;

    // Skip the logo hand-off while a modal dialog is open / scroll is locked, or it
    // strips the header's pointer-events and the open drawer inherits it.
    if (headerEl.querySelector('dialog[open]') || document.body.style.position === 'fixed') {
      headerEl.style.pointerEvents = '';
      return;
    }

    if (footerHiddenByHeader) {
      hideFloatingLogo();
      clearHeaderInlineStyles();
      return;
    }

    if (!startRect || !targetRect) measure();
    if (!startRect || !targetRect) return;

    const heroHeight = sectionEl.offsetHeight || window.innerHeight;
    const p = clamp01(window.scrollY / Math.max(1, heroHeight * 0.85));
    const handedOff = p >= HANDOFF_AT;

    if (handedOff) {
      linkEl.style.transform = '';
      darkImgEl.style.opacity = '0';
      lightImgEl.style.opacity = '0';
      wrapperEl.style.opacity = '0';
      wrapperEl.style.visibility = 'hidden';
      headerLogoEl.style.visibility = '';
      headerEl.style.opacity = '1';
      headerEl.style.pointerEvents = '';
      return;
    }

    wrapperEl.style.opacity = '1';
    wrapperEl.style.visibility = 'visible';
    headerLogoEl.style.visibility = 'hidden';

    const scale = startRect.width ? targetRect.width / startRect.width : 1;
    const tx = (targetRect.left - startRect.left) * p;
    const ty = (targetRect.top - startRect.top) * p;
    const s = 1 + (scale - 1) * p;

    linkEl.style.transform = `translate(${tx}px,${ty}px) scale(${s})`;

    const headerAlpha = clamp01((p - 0.3) / 0.3);
    headerEl.style.opacity = String(headerAlpha);
    headerEl.style.pointerEvents = headerAlpha < 0.5 ? 'none' : '';

    const cp = clamp01((p - 0.45) / 0.35);
    darkImgEl.style.opacity = String(cp);
    lightImgEl.style.opacity = String(1 - cp);
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(render);
  }

  /**
   * @param {Event} event
   */
  function onFooterVisibility(event) {
    if (!(event instanceof CustomEvent)) return;

    footerHiddenByHeader = event.detail?.visible === true;

    if (footerHiddenByHeader) {
      hideFloatingLogo();
      clearHeaderInlineStyles();
      return;
    }

    onScroll();
  }

  function onResize() {
    measure();
    render();
  }

  measure();
  render();

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize);
  headerEl.addEventListener('header:footer-visibility', onFooterVisibility);

  // dialog open/close don't bubble; re-render on them so the guard runs even when
  // the scroll-lock fires no scroll event.
  headerEl.addEventListener('dialog:open', onScroll, true);
  headerEl.addEventListener('dialog:close', onScroll, true);

  const imgs = /** @type {HTMLImageElement[]} */ ([darkImgEl, lightImgEl]);
  imgs.forEach((img) => {
    if (!img.complete) img.addEventListener('load', onResize, { once: true });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initHeroLogoTransition);
} else {
  initHeroLogoTransition();
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement && event.target.querySelector('[data-hero-section]')) {
    initHeroLogoTransition();
  }
});
